import { memo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import TuneIcon from "@mui/icons-material/Tune";
import { BooleanSwitchValue } from "consts/general";
import usePaymentMethodActions from "features/paymentMethods/useActions";
import useGeneralActions from "features/general/useActions";
import { NotificationSeverity } from "features/general/types";
import type { PaymentMethod, PaymentMethodResponse } from "features/paymentMethods/types";
import { usePaymentMethodsContext } from "../PaymentMethodsProvider";

const KEEP = 'keep';

type BulkValue = typeof KEEP | `${BooleanSwitchValue}`;

type BulkField = {
    readonly field: keyof Pick<PaymentMethod, 'isRefundable' | 'isAssignable'>;
    readonly label: string;
};

// Same flags as the "Method has refund support" / "Method can be available for merchant" checkboxes
const bulkFields: ReadonlyArray<BulkField> = [
    { field: 'isRefundable', label: 'Refund support' },
    { field: 'isAssignable', label: 'Available for merchants' }
];

const initialValues: Record<BulkField['field'], BulkValue> = {
    isRefundable: KEEP,
    isAssignable: KEEP
};

const BulkEditPaymentMethods = () => {
    const {
        getPaymentMethods,
        selectedIds,
        selectPage,
        clearSelection,
        upsertPaymentMethod
    } = usePaymentMethodsContext();
    const { updateOrCreatePaymentMethod } = usePaymentMethodActions();
    const { showNotication } = useGeneralActions();

    const [open, setOpen] = useState(false);
    const [values, setValues] = useState(initialValues);
    const [processing, setProcessing] = useState(false);
    const [failedNames, setFailedNames] = useState<string[]>([]);

    const paymentMethods = getPaymentMethods();
    const selectedMethods = paymentMethods.filter(({ coreId }) => selectedIds.has(String(coreId)));
    const isPageSelected = paymentMethods.length > 0 && selectedMethods.length === paymentMethods.length;

    const changes = Object.fromEntries(
        Object.entries(values)
            .filter(([, value]) => value !== KEEP)
            .map(([field, value]) => [field, Number(value) as BooleanSwitchValue])
    ) as Partial<Pick<PaymentMethod, BulkField['field']>>;
    const hasChanges = Object.keys(changes).length > 0;

    const close = () => {
        setOpen(false);
        setValues(initialValues);
        setFailedNames([]);
    };

    const apply = async () => {
        setProcessing(true);
        setFailedNames([]);

        const failed: string[] = [];

        try {
            for (const { coreId, name } of selectedMethods) {
                const [request] = updateOrCreatePaymentMethod({ coreId, ...changes });
                const response = await request as unknown as PaymentMethodResponse | undefined;

                if (response?.success && response.data) {
                    upsertPaymentMethod(response.data);
                } else {
                    failed.push(name);
                }
            }
        } finally {
            setProcessing(false);
        }

        if (failed.length) {
            setFailedNames(failed);
            return;
        }

        showNotication({
            severity: NotificationSeverity.Success,
            message: `${selectedMethods.length} payment method(s) updated`
        });
        clearSelection();
        close();
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 1
            }}
        >
            <FormControlLabel
                control={(
                    <Checkbox
                        checked={isPageSelected}
                        indeterminate={selectedMethods.length > 0 && !isPageSelected}
                        disabled={!paymentMethods.length}
                        onChange={(_, checked) => selectPage(checked)}
                        inputProps={{ 'aria-label': 'Select all payment methods on this page' }}
                    />
                )}
                label={`Select page (${paymentMethods.length})`}
            />
            <Button
                variant='outlined'
                startIcon={<TuneIcon />}
                disabled={!selectedMethods.length}
                onClick={() => setOpen(true)}
                data-test-id='paymentMethods.bulkEdit.open'
            >
                Bulk edit
            </Button>
            <Typography variant='caption' color='text.secondary'>
                {selectedMethods.length} selected
            </Typography>

            <Dialog open={open} onClose={processing ? undefined : close} fullWidth maxWidth='sm'>
                <DialogTitle>
                    Bulk edit · {selectedMethods.length} payment method(s)
                    <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                        {selectedMethods.map(({ name }) => name).join(', ')}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <Alert severity='info' sx={{ mb: 2 }}>
                        Only the fields you change are applied to the selected methods.
                    </Alert>
                    <Box sx={{ display: 'grid', gap: 2 }}>
                        {bulkFields.map(({ field, label }) => (
                            <TextField
                                key={field}
                                select
                                size='small'
                                label={label}
                                value={values[field]}
                                onChange={event => setValues(current => ({
                                    ...current,
                                    [field]: event.target.value as BulkValue
                                }))}
                                inputProps={{ 'data-test-id': `paymentMethods.bulkEdit.${field}` }}
                            >
                                <MenuItem value={KEEP}>Keep unchanged</MenuItem>
                                <MenuItem value={`${BooleanSwitchValue.On}`}>Enable</MenuItem>
                                <MenuItem value={`${BooleanSwitchValue.Off}`}>Disable</MenuItem>
                            </TextField>
                        ))}
                    </Box>
                    {failedNames.length > 0 && (
                        <Alert severity='error' sx={{ mt: 2 }}>
                            Could not update: {failedNames.join(', ')}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={close} disabled={processing}>
                        Cancel
                    </Button>
                    <LoadingButton
                        variant='contained'
                        loading={processing}
                        disabled={!hasChanges}
                        onClick={apply}
                        data-test-id='paymentMethods.bulkEdit.apply'
                    >
                        Apply to {selectedMethods.length}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default memo(BulkEditPaymentMethods);
